import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Rating} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import {RegionType, useRegions} from "@/store/region.c";
import Stack from "@mui/material/Stack";
import {CorporateFareOutlined} from "@mui/icons-material";
import ContactForm from "@/pages/Customer/ContactForm";
import React, {useMemo} from "react"
import {Grid, Box} from "@mui/material";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useRoleUsers} from "@/store/user.c";
import {useAuthInfo} from "@/store/account.c";
import {RoleUserVO} from "@/service/user.s";
import {useCreateCustomer, useQueryCustomer, useUpdateCustomer} from "@/service/customer.s";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {emptyValue, filterUsers, getSelectValues, setValues} from "@/helper/method";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {filter, isNumber} from "lodash";
import {TagOut} from "@/widget/TagOut/TagOut";
import isEmpty from "lodash/isEmpty";

const validationSchema = z.object({
    id: z.unknown().default(0),
    creatorId: z.number({message: "请选择负责人"}),
    creator: z.ostring(),
    tempCreator: z.object({username: z.string().default(''), id: z.number()}).optional(),
    statusId: z.number().nullable(),
    status: z.string({message: "请选择客户状态"}),
    industryId: z.number().nullable(),
    industry: z.string({message: "请选所在行业"}),
    customerName: z.string({message: "请填写客户名称"}).nonempty({message: "请填写客户名称"}),
    level: z.number({message: '请选择客户级别'}),
    followNames: z.ostring().nullable(),
    followIds: z.ostring().nullable(),
    tempFollows: z.array(z.object({username: z.string(), id: z.number()})).optional(),
    location: z.string({message: "请选择所在地区"}).nonempty({message: "请选择所在地区"}),
    locationId: z.string().nullable(),
    remark: z.string().optional(),
    tempLocation: z.object({title: z.string().default(''), value: z.string().default(""), group: z.string().default("")}),
    contacts: z.array(z.object({
        id: z.unknown().default(0),
        name: z.string({message: '请填写姓名'}).nonempty({message: "请填写姓名"}),
        mobile: z.string({message: "请填写联系电话"}).nonempty({message: "请填写联系电话"}),
        email: z.ostring(),
        firsted: z.number({message: "请选择首要联系人"}).default(1),
        gender: z.number({message: "请选择性别"}).default(1),
        post: z.string().nullable(),
        // email: z.string().email("请填写正确的邮箱格式").max(60, "邮箱长度不能超过60位").refine((val) => val.length > 0)
    })),
})

export type WriteCustomerForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteCustomer = () => {
    const regions = useRegions();
    const {industry, customerStatus} = useAllDict()
    const authInfo = useAuthInfo();
    const {id: drawerId, write} = useDrawerModal()
    const sellers: RoleUserVO[] = useRoleUsers(["marketing", "manager", "processer"])
    const {setToast} = useGlobalActions()
    const {mutateAsync: createCustomerMutate, isPending: createCustomerPending} = useCreateCustomer()
    const {mutateAsync: updateCustomerMutate, isPending: updateCustomerPending} = useUpdateCustomer()
    const {data: customerData} = useQueryCustomer({id: drawerId!}, isNumber(drawerId))
    const customer = customerData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()
    const initValues = () => {
        if (write == 'edit') {
            return {
                ...customer, tempLocation: {
                    title:
                    customer?.location,
                    value:
                    customer?.locationId,
                },
                tempCreator: {username: customer?.creator, id: customer?.creatorId},
                tempFollows: filterUsers(sellers, customer?.followIds ?? ''),
            }
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        reset,
        handleSubmit,
        formState: {errors},
    } = useForm<WriteCustomerForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WriteCustomerForm,
        defaultValues: {
            ...defaultValues,
            customerName: "",
            level: 2,
            statusId: 1,
            status: "意向",
            creatorId: authInfo.id,
            creator: authInfo.username,
            tempCreator: {id: authInfo.id, username: authInfo.username},
            contacts: [{firsted: 1}],
        },
    });

    const contacts = useWatch({
        control,
        name: "contacts"
    })

    const firstedErrMsg: string = useMemo(() => {
        const filtered = filter(contacts, item => item.firsted == 1)
        if (filtered.length > 1) {
            return "首要联系人只能选择一个"
        }
        return ""
    }, [contacts]);

    const onSubmit = async (values: WriteCustomerForm) => {
        if (!isEmpty(firstedErrMsg)) {
            return
        }
        const result = write == 'add' ? await createCustomerMutate(values) : await updateCustomerMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={1.5} direction={"column"}>
                <Grid container spacing={3}>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel htmlFor="customerName">客户名称</FormLabel>
                            <TextField
                                error={!!errors?.customerName}
                                helperText={errors?.customerName?.message}
                                id="customerName"
                                {...register("customerName")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 40},
                                        endAdornment: <CorporateFareOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="输入客户名称"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>负责人</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    options={sellers}
                                    value={value}
                                    onChange={(_, value: RoleUserVO | null) => {
                                        setValue("creatorId", value!.id)
                                        setValue("creator", value?.username)
                                        onChange(value)
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id == value.id}
                                    getOptionKey={option => option.username}
                                    getOptionLabel={option => option.username}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField error={!!errors?.creatorId}
                                                   helperText={errors?.creatorId?.message}
                                                   variant="outlined"
                                                   placeholder={"选择负责人"} {...params} />
                                    )}
                                />
                            }} name={"tempCreator"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>协作人</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    multiple={true}
                                    options={sellers}
                                    value={value}
                                    disableListWrap={true}
                                    onChange={(_, options) => {
                                        const values = getSelectValues(options, {key: "username", val: 'id'}, {key: "followIds", val: "followNames"})
                                        setValues(values, setValue as any)
                                        onChange(options)
                                    }}
                                    renderValue={(values) => {
                                        return values?.map((value: RoleUserVO, index: number) => <TagOut key={index} index={index} label={value.username}/>)
                                    }}
                                    isOptionEqualToValue={(option, value) => option.username === value.username}
                                    disableCloseOnSelect={true}
                                    getOptionLabel={(option: RoleUserVO) => option.username}
                                    getOptionKey={(option: RoleUserVO) => option.username}
                                    renderInput={(params) => (
                                        <TextField
                                            sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                            helperText={errors?.followIds?.message} placeholder={"选择协作人"} {...params} size={'small'}/>
                                    )}
                                />
                            }}
                                        name={"tempFollows"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>客户状态</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                options={customerStatus}
                                value={value as any}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("statusId", option!.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.status}
                                               helperText={errors?.status?.message}
                                               variant="outlined"
                                               placeholder={"选择客户状态"} {...params} />
                                )}
                            />} name={"status"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>客户行业</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                options={industry}
                                value={value as any}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("industryId", option?.value || 0)
                                    onChange(option?.label)
                                }}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.industry}
                                               helperText={errors?.industry?.message} placeholder={"选择客户行业"} {...params} />
                                )}
                            />} name={"industry"}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel htmlFor="level">客户级别</FormLabel>
                            <Controller
                                control={control}
                                name={"level"}
                                render={({field: {onChange, value}}) => {
                                    return (
                                        <TextField
                                            // error={!!errors?.account}
                                            // helperText={errors?.account?.message}
                                            id="level"
                                            placeholder="选择客户级别"
                                            select={true}
                                            value={value}
                                            onChange={(event) => {
                                                onChange(event.target.value);
                                            }}
                                            variant="outlined"
                                        >
                                            {Array.from(new Array(5)).map(
                                                (item: number, index: number) => (
                                                    <MenuItem value={index + 1} key={index}>
                                                        <Rating value={index + 1} readOnly={true}/>
                                                    </MenuItem>
                                                ),
                                            )}
                                        </TextField>
                                    );
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true}>
                            <FormLabel>所在区域</FormLabel>
                            <Controller
                                control={control}
                                name={"tempLocation"}
                                render={({field: {onChange, value}}) => (
                                    <Autocomplete
                                        onChange={(_, option: RegionType | null) => {
                                            setValue("locationId", option?.value || '')
                                            setValue("location", option?.title || '')
                                            onChange(option)
                                        }}
                                        value={value || null}
                                        groupBy={(option: RegionType) => option.group}
                                        getOptionLabel={(option: RegionType) => option.title}
                                        getOptionKey={(option: RegionType) => option.value}
                                        options={regions}
                                        fullWidth
                                        renderInput={(params) => (
                                            <TextField
                                                error={!!errors?.location}
                                                helperText={errors?.location?.message}
                                                placeholder={"选择所在区域"}
                                                {...params}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        <FormControl fullWidth={true}>
                            <FormLabel>备注</FormLabel>
                            <TextField
                                multiline
                                rows={3}
                                {...register("remark")}
                                slotProps={{
                                    input: {
                                        sx: {minHeight: 80},
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="输入备注"
                                fullWidth
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                <ContactForm firstedErrMsg={firstedErrMsg} register={register} errors={errors} control={control}/>
                <DrawerFooter loading={createCustomerPending || updateCustomerPending} onReset={() => {
                    reset(defaultValues)
                }}/>
            </Stack>
        </Box>
    );
};
